import React, {
  useEffect,
  useState,
  lazy,
  Suspense
} from "react";
import { Link } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import Swal from "sweetalert2";
import Select from "react-select";
import * as XLSX from "xlsx";
import PoServices from "../../../services/PoServices";
import ReceivedOrderModal from "./ReceivedOrderModal";
import PoRevisionPrintModal from "./PoRevisionPrintModal";
import Pagination from "../Pagenation/Pagenation";
import Loader from "../Loader/loader";


// ============================================================
// LAZY LOAD PO AMENDMENT MODAL
// ============================================================

const preloadRateChangeModal = () =>
  import("./RateChangeRequestModal");

const RateChangeRequestModal = lazy(() =>
  import("./RateChangeRequestModal")
);


// ============================================================
// PO VIEW OPTIONS
// ============================================================

const PO_VIEW_OPTIONS = [
  {
    value: "Confirmed-Generated",
    label: "Printable PO"
  },
  {
    value: "Partially-Received",
    label: "Partially Received"
  },
  {
    value: "Order-Received",
    label: "Order Received"
  }
];

const compactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderRadius: 18
  }),
  valueContainer: (base) => ({
    ...base,
    height: 40,
    padding: "0 10px"
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 40
  })
};

const PO_SEARCH_OPTIONS = [
  {
    value: "poNumber",
    label: "PO Number"
  },
  {
    value: "requisitionNumber",
    label: "Requisition Number"
  },
  {
    value: "supplier",
    label: "Supplier"
  },
  {
    value: "partNo",
    label: "Part Number"
  },
  {
    value: "description",
    label: "Description"
  },
  {
    value: "store",
    label: "Store"
  },
  {
    value: "createdBy",
    label: "Created By"
  },
  {
    value: "revision",
    label: "Revision"
  },
  {
    value: "status",
    label: "Status"
  }
];



// ============================================================
// COMPONENT
// ============================================================

const PurchaseOrderManagement = () => {

  // ==========================================================
  // PO VIEW
  // ==========================================================

  const [
    selectedStatus,
    setSelectedStatus
  ] = useState("Confirmed-Generated");


  // ==========================================================
  // MAIN DATA
  // ==========================================================

  const [
    purchaseOrders,
    setPurchaseOrders
  ] = useState([]);
  const [
    searchPurchaseOrders,
    setSearchPurchaseOrders
  ] = useState([]);

  const [
    searchDataLoaded,
    setSearchDataLoaded
  ] = useState(false);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    refreshKey,
    setRefreshKey
  ] = useState(0);


  // ==========================================================
  // FILTER / TABLE
  // ==========================================================

  const [
    open,
    setOpen
  ] = useState(true);

  const [
    open2,
    setOpen2
  ] = useState(true);

  const [
    searchField,
    setSearchField
  ] = useState("");

  const [
    searchTerm,
    setSearchTerm
  ] = useState("");

  const [
    submittedSearch,
    setSubmittedSearch
  ] = useState("");

  const [
    sort,
    setSort
  ] = useState(100);

  const [
    activePage,
    setActivePage
  ] = useState(0);


  // ==========================================================
  // REVISION SORT
  // ==========================================================

  const [
    revisionSortOrder,
    setRevisionSortOrder
  ] = useState(null);


  // ==========================================================
  // VIEW ITEMS MODAL
  // ==========================================================

  const [
    viewItemModal,
    setViewItemModal
  ] = useState(false);

  const [
    selectedItem,
    setSelectedItem
  ] = useState(null);


  // ==========================================================
  // PRINT MODAL
  // ==========================================================

  const [
    printModal,
    setPrintModal
  ] = useState(false);

  const [
    selectedPrintItem,
    setSelectedPrintItem
  ] = useState(null);

  const [
    printLoadingId,
    setPrintLoadingId
  ] = useState("");


  // ==========================================================
  // PO AMENDMENT
  // ==========================================================

  const [
    rateChangeModalOpen,
    setRateChangeModalOpen
  ] = useState(false);

  const [
    selectedRateChangePO,
    setSelectedRateChangePO
  ] = useState(null);


  // ==========================================================
  // USER ROLE
  // ==========================================================

  let userRole = "";

  try {

    const userDetails =
      JSON.parse(
        localStorage.getItem(
          "userDetails"
        ) || "{}"
      );

    userRole =
      userDetails
        ?.data
        ?.user
        ?.role ||
      "";

  } catch (error) {

    userRole = "";
  }


  // ==========================================================
  // VIEW HELPERS
  // ==========================================================

  const isPrintablePO =
    selectedStatus ===
    "Confirmed-Generated";

  const isPartiallyReceived =
    selectedStatus ===
    "Partially-Received";

  const isOrderReceived =
    selectedStatus ===
    "Order-Received";


  // ==========================================================
  // PO STATUS DISPLAY NAME
  // ==========================================================

  const getDisplayStatus = (status) => {

    switch (status) {

      case "Generated":
        return "Generated";

      case "Assigned-To-Maker":
        return "Pending with Maker";

      case "Assigned-To-Checker":
        return "Pending with Checker";

      case "Assigned-To-SuperAdmin":
        return "Pending with HO";

      case "Confirmed-Generated":
        return "Pending with Supplier";

      case "Order-Received":
        return "Order Received";

      case "Partially-Received":
        return "Partially Pending with Supplier";

      case "Bill-Generated":
        return "Under Billing";

      case "PaymentPending":
        return "Payment Pending";

      case "Completed":
        return "Completed";

      case "Supplier-Denied":
        return "Supplier Denied";

      case "Rate-Revision-Pending":
        return "PO Amendment Pending";

      default:
        return status || "N/A";
    }
  };


  // ==========================================================
  // PAGE TITLE
  // ==========================================================

  const getViewTitle = () => {

    if (isPrintablePO) {
      return "Printable Purchase Orders";
    }

    if (isPartiallyReceived) {
      return "Partially Received Purchase Orders";
    }

    if (isOrderReceived) {
      return "Received Purchase Orders";
    }

    return "Purchase Orders";
  };


  // ==========================================================
  // FETCH PURCHASE ORDERS
  // ==========================================================

  useEffect(() => {

    const fetchPurchaseOrders =
      async () => {

        try {

          setLoading(true);

          const res =
            await PoServices
              .AllPOByStatus(
                selectedStatus
              );

          const poList =
            res?.data ||
            [];

          console.log(
            "PO VIEW:",
            selectedStatus
          );

          console.log(
            "PO DATA:",
            poList
          );

          setPurchaseOrders(
            poList
          );

        } catch (error) {

          console.error(
            "PO FETCH ERROR:",
            error
          );

          Swal.fire(
            "Error",
            "Failed to fetch purchase orders",
            "error"
          );

          setPurchaseOrders([]);

        } finally {

          setLoading(false);
        }
      };


    fetchPurchaseOrders();

  }, [
    selectedStatus,
    refreshKey
  ]);


  // ==========================================================
  // RESET PAGE WHEN FILTER CHANGES
  // ==========================================================

  useEffect(() => {

    setActivePage(0);

  }, [
    searchTerm,
    sort,
    selectedStatus
  ]);


  // ==========================================================
  // SEARCH + REVISION SORT
  // ==========================================================

  const sourcePurchaseOrders =
    submittedSearch
      ? searchPurchaseOrders
      : purchaseOrders;


  const filteredSearch =
    [...sourcePurchaseOrders]

      .filter((po) => {

        const requisitionNumber =
          String(
            po?.requisitionNo
              ?.requisitionNo ||
            ""
          );

        const poNumber =
          String(
            po?.poNumber ||
            ""
          );

        const supplierName =
          po?.items
            ?.map((item) =>
              String(
                item?.supplier
                  ?.name ||
                ""
              ).toLowerCase()
            )
            .join(" ") ||
          "";

        const createdBy =
          String(
            po?.createdBy
              ?.name ||
            ""
          ).toLowerCase();

        const storeLocation =
          String(
            po?.requisitionNo
              ?.store
              ?.name ||
            ""
          ).toLowerCase();

        const partNos =
          po?.items
            ?.map((item) =>
              String(
                item?.partNo ||
                ""
              ).toLowerCase()
            )
            .join(" ") ||
          "";

        const descriptions =
          po?.items
            ?.map((item) =>
              String(
                item?.description ||
                ""
              ).toLowerCase()
            )
            .join(" ") ||
          "";


        // ==========================================
        // REVISION SEARCH
        // ==========================================

        const revisionNo =
          Number(
            po?.commercialRevisionCount ||
            0
          );

        const isRevised =
          revisionNo > 0;

        const revisionText =
          isRevised
            ? `revised revision ${revisionNo} rev ${revisionNo}`
            : "new original";


        // ==========================================
        // STATUS SEARCH
        // ==========================================

        const statusText =
          String(
            po?.status ||
            ""
          )
            .toLowerCase()
            .replaceAll("-", " ");

        const displayStatusText =
          getDisplayStatus(
            po?.status
          ).toLowerCase();


        const search =
          submittedSearch
            .toLowerCase()
            .trim();

        switch (searchField) {

          case "poNumber":
            return poNumber
              .toLowerCase()
              .includes(search);

          case "requisitionNumber":
            return requisitionNumber
              .toLowerCase()
              .includes(search);

          case "supplier":
            return supplierName
              .includes(search);

          case "partNo":
            return partNos
              .includes(search);

          case "description":
            return descriptions
              .includes(search);

          case "store":
            return storeLocation
              .includes(search);

          case "createdBy":
            return createdBy
              .includes(search);

          case "revision":
            return revisionText
              .includes(search);

          case "status":
            return (
              statusText.includes(search) ||
              displayStatusText.includes(search)
            );

          default:
            return true;
        }

      })


      // ==========================================
      // REVISION ASC / DESC
      // ==========================================

      .sort((a, b) => {

        if (!revisionSortOrder) {
          return 0;
        }

        /*
         * New = 0
         * Rev 1 = 1
         * Rev 2 = 2
         * Rev 3 = 3
         */

        const revisionA =
          Number(
            a?.commercialRevisionCount ||
            0
          );

        const revisionB =
          Number(
            b?.commercialRevisionCount ||
            0
          );


        if (
          revisionSortOrder ===
          "asc"
        ) {
          return (
            revisionA -
            revisionB
          );
        }

        return (
          revisionB -
          revisionA
        );
      });


  // ==========================================================
  // PAGINATION
  // ==========================================================

  const pagination =
    Math.ceil(
      filteredSearch.length /
      sort
    );


  const paginatedPurchaseOrders =
    filteredSearch.slice(
      activePage * sort,
      (activePage + 1) * sort
    );


  const onClick = (page) => {

    setActivePage(page);
  };


  // ==========================================================
  // REVISION SORT CLICK
  // ==========================================================

  const handleRevisionSort = () => {

    setRevisionSortOrder(
      (prev) => {

        if (prev === "asc") {
          return "desc";
        }

        return "asc";
      }
    );

    setActivePage(0);
  };


  // ==========================================================
  // CHANGE PO VIEW
  // ==========================================================

  const handleStatusChange =
    (selectedOption) => {

      if (!selectedOption) {
        return;
      }

      setSelectedStatus(
        selectedOption.value
      );

      setSearchField("");

      setSearchTerm("");

      setSubmittedSearch("");

      setRevisionSortOrder(
        null
      );

      setActivePage(0);
    };


  // ==========================================================
  // VIEW ITEMS
  // ==========================================================

  const handleViewItem =
    (e, row) => {

      e?.stopPropagation();

      setSelectedItem(
        row
      );

      setViewItemModal(
        true
      );
    };


  // ==========================================================
  // PRINT PO WITH REVISION HISTORY
  // ==========================================================

  const handleViewClick =
    async (poDetails) => {

      const poId =
        poDetails?._id;

      if (!poId) {

        Swal.fire(
          "Error",
          "Purchase Order ID is missing.",
          "error"
        );

        return;
      }


      if (printLoadingId) {
        return;
      }


      try {

        setPrintLoadingId(
          String(poId)
        );

        setSelectedPrintItem(
          null
        );


        const response =
          await PoServices
            .getPurchaseOrderPrintHistory(
              poId
            );


        const printData =
          response?.data?.data ||
          response?.data ||
          null;


        const versions =
          Array.isArray(
            printData?.versions
          )
            ? printData.versions
            : [];


        if (!printData) {

          Swal.fire(
            "Print Data Missing",
            "Purchase Order print data was not found.",
            "warning"
          );

          return;
        }


        if (!versions.length) {

          Swal.fire(
            "No PO Versions",
            "Original or revised Purchase Order versions were not found.",
            "warning"
          );

          return;
        }


        setSelectedPrintItem(
          printData
        );

        setPrintModal(
          true
        );

      } catch (error) {

        console.error(
          "PO PRINT ERROR:",
          error
        );

        Swal.fire(
          "Error",
          error?.response
            ?.data
            ?.message ||
          error?.message ||
          "Unable to load Purchase Order print history.",
          "error"
        );

      } finally {

        setPrintLoadingId("");
      }
    };


  // ==========================================================
  // PO AMENDMENT REQUEST
  // PRINTABLE PO + PO MAKER ONLY
  // ==========================================================

  const handleRateChangeRequest =
    (po) => {

      if (!po?._id) {

        Swal.fire(
          "Error",
          "Purchase Order ID is missing.",
          "error"
        );

        return;
      }


      if (
        !Array.isArray(
          po?.items
        ) ||
        !po.items.length
      ) {

        Swal.fire(
          "No Items",
          "No items found in this Purchase Order.",
          "warning"
        );

        return;
      }


      setSelectedRateChangePO(
        po
      );

      setRateChangeModalOpen(
        true
      );
    };


  // ==========================================================
  // EXCEL EXPORT
  // EXPORT COMPLETE FILTERED RESULT
  // NOT CURRENT PAGE ONLY
  // ==========================================================

  const exportToExcel = () => {

    if (
      !filteredSearch ||
      filteredSearch.length === 0
    ) {

      Swal.fire(
        "Info",
        "No purchase orders to export.",
        "info"
      );

      return;
    }


    const dataForExcel =
      filteredSearch.map(
        (row, index) => {

          const revisionNo =
            Number(
              row
                ?.commercialRevisionCount ||
              0
            );

          return {

            "#":
              index + 1,

            "PO View":
              PO_VIEW_OPTIONS.find(
                option =>
                  option.value ===
                  selectedStatus
              )?.label ||
              selectedStatus,

            "Requisition":
              row
                ?.requisitionNo
                ?.requisitionNo ||
              row
                ?.requisitionNo
                ?._id ||
              "N/A",

            "PO Number":
              row?.poNumber ||
              "N/A",

            "Revision":
              revisionNo > 0
                ? `Rev ${revisionNo}`
                : "New",

            "Part No":
              row?.items
                ?.map(
                  item =>
                    item?.partNo
                )
                .join(", ") ||
              "N/A",

            "Description":
              row?.items
                ?.map(
                  item =>
                    item?.description
                )
                .join(", ") ||
              "N/A",

            "Qty":
              row?.items
                ?.map(
                  item =>
                    item?.approveQty
                )
                .join(", ") ||
              "N/A",

            "Rate":
              row?.items
                ?.map(
                  item =>
                    item?.rate
                )
                .join(", ") ||
              "N/A",

            "Store":
              row
                ?.requisitionNo
                ?.store
                ?.name ||
              "N/A",

            "Supplier":
              row?.items
                ?.map(
                  item =>
                    item
                      ?.supplier
                      ?.name
                )
                .join(", ") ||
              "N/A",

            "Delivery Address":
              [
                row?.deliveryAddress
                  ?.street,
                row?.deliveryAddress
                  ?.city,
                row?.deliveryAddress
                  ?.state,
                row?.deliveryAddress
                  ?.pincode,
                row?.deliveryAddress
                  ?.country
              ]
                .filter(Boolean)
                .join(", "),

            "Created At":
              row?.createdAt
                ? new Date(
                  row.createdAt
                ).toLocaleDateString(
                  "en-IN"
                )
                : "N/A",

            "Items Count":
              row?.items?.length ||
              0
          };
        }
      );


    const worksheet =
      XLSX.utils.json_to_sheet(
        dataForExcel
      );

    const workbook =
      XLSX.utils.book_new();


    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "PurchaseOrders"
    );


    const fileName =
      isPrintablePO
        ? "Printable_Purchase_Orders.xlsx"
        : isPartiallyReceived
          ? "Partially_Received_POs.xlsx"
          : "Received_Purchase_Orders.xlsx";


    XLSX.writeFile(
      workbook,
      fileName
    );
  };

  const handleSearchChange = (event) => {

    setSearchTerm(
      event.target.value
    );
  };
  // ==========================================================
  // SUBMIT GLOBAL PO SEARCH
  // ==========================================================

  const handleSearchSubmit =
    async () => {

      const value =
        searchTerm.trim();

      // ==========================================
      // SEARCH FIELD REQUIRED
      // ==========================================

      if (!searchField) {

        Swal.fire(
          "Search",
          "Please select Search By first.",
          "info"
        );

        return;
      }


      // ==========================================
      // SEARCH VALUE REQUIRED
      // ==========================================

      if (!value) {

        Swal.fire(
          "Search",
          "Please enter a search value.",
          "info"
        );

        return;
      }


      setActivePage(0);

      try {

        setLoading(true);


        // ==========================================
        // LOAD GLOBAL PO DATA ONLY ONCE
        // ==========================================

        if (!searchDataLoaded) {

          const res =
            await PoServices
              .AllPOByStatus(
                "PO_SEARCH_ALL"
              );

          const poList =
            res?.data ||
            [];

          setSearchPurchaseOrders(
            poList
          );

          setSearchDataLoaded(
            true
          );
        }


        // ==========================================
        // APPLY SEARCH AFTER SUBMIT
        // ==========================================

        setSubmittedSearch(
          value
        );

      } catch (error) {

        console.error(
          "PO GLOBAL SEARCH ERROR:",
          error
        );

        Swal.fire(
          "Error",
          "Failed to search purchase orders.",
          "error"
        );

      } finally {

        setLoading(false);
      }
    };

  if (loading) {
    return (
      <Loader />
    )
  }

  return (
    <>

      {/* =====================================================
          PO AMENDMENT MODAL
      ===================================================== */}

      <Suspense
        fallback={
          rateChangeModalOpen
            ? <Loader />
            : null
        }
      >

        {selectedRateChangePO && (
          <RateChangeRequestModal
            show={
              rateChangeModalOpen
            }
            purchaseOrder={
              selectedRateChangePO
            }
            onHide={() => {

              setRateChangeModalOpen(
                false
              );

              setSelectedRateChangePO(
                null
              );
            }}
            onSuccess={() => {

              setRateChangeModalOpen(
                false
              );

              setSelectedRateChangePO(
                null
              );

              setSearchPurchaseOrders(
                []
              );

              setSearchDataLoaded(
                false
              );

              setRefreshKey(
                prev =>
                  prev + 1
              );
            }}
          />
        )}

      </Suspense>


      {/* =====================================================
          VIEW ITEMS MODAL
      ===================================================== */}

      {selectedItem && (
        <ReceivedOrderModal
          show={
            viewItemModal
          }
          handleClose={() => {

            setViewItemModal(
              false
            );

            setSelectedItem(
              null
            );
          }}
          test={
            setRefreshKey
          }
          admin={
            selectedItem
          }
        />
      )}


      {/* =====================================================
          PRINT / REVISION MODAL
      ===================================================== */}

      {selectedPrintItem && (
        <PoRevisionPrintModal
          show={
            printModal
          }
          onHide={() => {

            setPrintModal(
              false
            );

            setSelectedPrintItem(
              null
            );
          }}
          printHistory={
            selectedPrintItem
          }
        />
      )}


      {/* =====================================================
          MAIN PAGE
      ===================================================== */}

      <div className="row">

        <div className="col-xl-12">


          {/* =================================================
              FILTER
          ================================================= */}

          <div className="filter cm-content-box box-primary">

            <div className="content-title">

              <div className="cpa">

                <i className="fas fa-filter me-2" />

                Purchase Order Filter

              </div>


              <div className="tools">

                <Link
                  to="#"
                  className={
                    `SlideToolHeader ${open
                      ? "collapse"
                      : "expand"
                    }`
                  }
                  onClick={() =>
                    setOpen(
                      !open
                    )
                  }
                >

                  <i className="fas fa-angle-up" />

                </Link>

              </div>

            </div>


            <Collapse in={open}>

              <div className="cm-content-body form excerpt">

                <div className="card-body">

                  <div className="row">


                    {/* =====================================
                        PO VIEW DROPDOWN
                    ===================================== */}

                    <div className="col-md-4 mb-3">

                      <label
                        className="form-label fw-semibold"
                      >
                        PO View
                      </label>

                      <Select
                        value={
                          PO_VIEW_OPTIONS.find(
                            option =>
                              option.value === selectedStatus
                          )
                        }
                        onChange={handleStatusChange}
                        options={PO_VIEW_OPTIONS}
                        className="po-filter-select"
                        classNamePrefix="po-filter"
                        styles={compactSelectStyles}
                        isSearchable={true}
                      />

                    </div>

                    {/* =====================================
    SEARCH
===================================== */}

                    <div className="col-md-8 mb-3">

                      <label className="form-label fw-semibold">
                        Search Purchase Order
                      </label>

                      <div className="row g-2">

                        {/* =====================================
        SEARCH BY
    ===================================== */}

                        <div className="col-12 col-md-4 col-lg-3">

                          <Select
                            value={
                              PO_SEARCH_OPTIONS.find(
                                option =>
                                  option.value === searchField
                              ) || null
                            }
                            onChange={(selectedOption) => {

                              setSearchField(
                                selectedOption?.value || ""
                              );

                              // Clear previous search
                              setSearchTerm("");
                              setSubmittedSearch("");
                              setActivePage(0);

                            }}
                            options={PO_SEARCH_OPTIONS}
                            placeholder="Select Search By"
                            className="po-filter-select"
                            classNamePrefix="po-filter"
                            styles={compactSelectStyles}
                            isSearchable={true}
                            isClearable={true}
                          />

                        </div>


                        {/* =====================================
        SEARCH VALUE
    ===================================== */}

                        <div className="col-12 col-md-8 col-lg-6">

                          <input
                            type="text"
                            className="form-control"
                            style={{ height: 40, minHeight: 40 }}
                            placeholder={
                              searchField
                                ? "Enter search value"
                                : "Select Search By first"
                            }
                            value={searchTerm}
                            disabled={!searchField}
                            onChange={
                              handleSearchChange
                            }
                            onKeyDown={(e) => {

                              if (
                                e.key === "Enter" &&
                                searchField &&
                                searchTerm.trim()
                              ) {
                                handleSearchSubmit();
                              }

                            }}
                          />

                        </div>


                        {/* =====================================
        SEARCH / CLEAR
    ===================================== */}

                        <div className="col-12 col-lg-3">

                          <div className="d-flex align-items-center gap-2" style={{ minHeight: 40 }}> 

                            <button
                              type="button"
                              className="btn btn-primary d-inline-flex align-items-center justify-content-center"
                              style={{ height: 40, padding: "0 14px", fontSize: 14, whiteSpace: "nowrap" }}
                              disabled={
                                !searchField ||
                                !searchTerm.trim()
                              }
                              onClick={
                                handleSearchSubmit
                              }
                            >
                              <i className="fa fa-search me-1" />
                              Search
                            </button>


                            {(searchTerm || submittedSearch) && (

                              <button
                                type="button"
                                className=" btn btn-danger d-inline-flex align-items-center justify-content-center"
                                style={{ width: 40, height: 40, padding: 0, flexShrink: 0 }}
                                title="Clear Search"
                                aria-label="Clear search"
                                onClick={() => {

                                  setSearchTerm("");
                                  setSubmittedSearch("");
                                  setActivePage(0);

                                }}
                              >
                                <i className="fa fa-times" />
                              </button>

                            )}

                          </div>

                        </div>

                      </div>

                    </div>


                  </div>

                </div>

              </div>

            </Collapse>

          </div>


          {/* =================================================
              TABLE
          ================================================= */}

          <div className="filter cm-content-box box-primary">

            <div className="content-title">

              <div className="cpa">

                <i className="fa fa-list me-2" />

                {getViewTitle()}

                <span className="badge bg-orange ms-2">

                  {filteredSearch.length}

                </span>

              </div>


              <div className="d-flex align-items-center gap-2">



                <div className="tools">

                  <Link
                    to="#"
                    className={
                      `SlideToolHeader ${open2
                        ? "collapse"
                        : "expand"
                      }`
                    }
                    onClick={() =>
                      setOpen2(
                        !open2
                      )
                    }
                  >

                    <i className="fas fa-angle-up" />

                  </Link>

                </div>

              </div>

            </div>


            <Collapse in={open2}>

              <div className="cm-content-body form excerpt">

                <div className="card-body">
                  <div className="d-flex justify-content-end me-2 mb-3">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={
                        exportToExcel
                      }
                    >

                      <i className="fa fa-file-excel me-1" />

                      Download Excel

                    </button>
                  </div>
                  <div className="table-responsive">

                    <div
                      id="content_wrapper"
                      className="dataTables_wrapper no-footer"
                    >

                      <div
                        className="scrolltable"
                      >

                        <table
                          className="
                                table
                                table-bordered
                                table-responsive-lg
                                table-striped
                                table-condensed
                                flip-content
                              "
                          id="active-users-data"
                        >

                          {/* =============================
                                  TABLE HEADER
                              ============================= */}

                          <thead
                            className="sticky-table-header"
                          >

                            <tr>

                              <th>
                                #
                              </th>

                              <th>
                                Requisition
                              </th>

                              <th>
                                PO
                              </th>
                              <th>
                                Status
                              </th>
                              {/* =========================
                                      REVISION
                                  ========================= */}

                              <th
                                onClick={
                                  handleRevisionSort
                                }
                                style={{
                                  cursor:
                                    "pointer",
                                  whiteSpace:
                                    "nowrap"
                                }}
                              >

                                Revision{" "}

                                {
                                  revisionSortOrder ===
                                    "asc"
                                    ? "🔼"
                                    : revisionSortOrder ===
                                      "desc"
                                      ? "🔽"
                                      : "⇅"
                                }

                              </th>


                              <th>
                                Part No
                              </th>

                              <th>
                                Description
                              </th>

                              <th>
                                Qty
                              </th>

                              <th>
                                Rate
                              </th>

                              <th>
                                Store
                              </th>

                              <th>
                                Supplier
                              </th>

                              <th>
                                Delivery Address
                              </th>

                              <th>
                                Created At
                              </th>

                              <th>
                                Items
                              </th>

                              <th>
                                View Items
                              </th>

                              <th>
                                Print
                              </th>


                              {/* =========================
                                      PRINTABLE PO ONLY
                                  ========================= */}

                              {isPrintablePO &&
                                userRole ===
                                "pomaker" && (

                                  <th>
                                    PO Amendment
                                  </th>

                                )}

                            </tr>

                          </thead>


                          {/* =============================
                                  TABLE BODY
                              ============================= */}

                          <tbody>

                            {paginatedPurchaseOrders.length >
                              0 ? (

                              paginatedPurchaseOrders.map(
                                (
                                  row,
                                  index
                                ) => {

                                  const revisionNo =
                                    Number(
                                      row
                                        ?.commercialRevisionCount ||
                                      0
                                    );

                                  return (

                                    <tr
                                      key={
                                        row?._id ||
                                        index
                                      }
                                    >

                                      {/* # */}

                                      <td>

                                        {
                                          activePage *
                                          sort +
                                          index +
                                          1
                                        }

                                      </td>


                                      {/* REQUISITION */}

                                      <td>

                                        {
                                          row
                                            ?.requisitionNo
                                            ?.requisitionNo ||
                                          row
                                            ?.requisitionNo
                                            ?._id ||
                                          "N/A"
                                        }

                                      </td>


                                      {/* PO */}

                                      <td>

                                        {
                                          row
                                            ?.poNumber ||
                                          "N/A"
                                        }

                                      </td>
                                      {/* STATUS */}

                                      <td>
                                        <span
                                          className={
                                            row?.status === "Confirmed-Generated"
                                              ? "badge bg-success"
                                              : row?.status === "Partially-Received"
                                                ? "badge bg-warning text-dark"
                                                : row?.status === "Order-Received"
                                                  ? "badge bg-primary"
                                                  : "badge bg-secondary"
                                          }
                                        >
                                          {getDisplayStatus(row?.status)}
                                        </span>
                                      </td>


                                      {/* REVISION */}

                                      <td>

                                        {revisionNo >
                                          0 ? (

                                          <span className="badge bg-warning text-dark">

                                            Rev{" "}
                                            {
                                              revisionNo
                                            }

                                          </span>

                                        ) : (

                                          <span className="badge bg-success">

                                            New

                                          </span>

                                        )}

                                      </td>


                                      {/* PART NO */}

                                      <td>

                                        {row?.items?.map(
                                          (
                                            item,
                                            idx
                                          ) => (

                                            <div
                                              key={
                                                idx
                                              }
                                            >

                                              {
                                                item
                                                  ?.partNo ||
                                                "N/A"
                                              }

                                            </div>

                                          )
                                        )}

                                      </td>


                                      {/* DESCRIPTION */}

                                      <td className="scrollable-description">

                                        {row?.items?.map(
                                          (
                                            item,
                                            idx
                                          ) => (

                                            <div
                                              key={
                                                idx
                                              }
                                            >

                                              {
                                                item
                                                  ?.description ||
                                                "N/A"
                                              }

                                            </div>

                                          )
                                        )}

                                      </td>


                                      {/* QTY */}

                                      <td>

                                        {row?.items?.map(
                                          (
                                            item,
                                            idx
                                          ) => (

                                            <div
                                              key={
                                                idx
                                              }
                                            >

                                              {
                                                item
                                                  ?.approveQty ??
                                                "N/A"
                                              }

                                            </div>

                                          )
                                        )}

                                      </td>


                                      {/* RATE */}

                                      <td>

                                        {row?.items?.map(
                                          (
                                            item,
                                            idx
                                          ) => (

                                            <div
                                              key={
                                                idx
                                              }
                                            >

                                              {
                                                item
                                                  ?.rate ??
                                                "N/A"
                                              }

                                            </div>

                                          )
                                        )}

                                      </td>


                                      {/* STORE */}

                                      <td className="scrollable-description">

                                        {
                                          row
                                            ?.requisitionNo
                                            ?.store
                                            ?.name ||
                                          "N/A"
                                        }

                                      </td>


                                      {/* SUPPLIER */}

                                      <td className="scrollable-description">

                                        {row?.items?.map(
                                          (
                                            item,
                                            idx
                                          ) => (

                                            <div
                                              key={
                                                idx
                                              }
                                            >

                                              {
                                                item
                                                  ?.supplier
                                                  ?.name ||
                                                "N/A"
                                              }

                                            </div>

                                          )
                                        )}

                                      </td>


                                      {/* DELIVERY ADDRESS */}

                                      <td className="scrollable-description">

                                        {
                                          row
                                            ?.deliveryAddress
                                            ?.street ||
                                          ""
                                        }

                                        {", "}

                                        {
                                          row
                                            ?.deliveryAddress
                                            ?.city ||
                                          ""
                                        }

                                        <br />

                                        {
                                          row
                                            ?.deliveryAddress
                                            ?.state ||
                                          ""
                                        }

                                        {" - "}

                                        {
                                          row
                                            ?.deliveryAddress
                                            ?.pincode ||
                                          ""
                                        }

                                        <br />

                                        {
                                          row
                                            ?.deliveryAddress
                                            ?.country ||
                                          ""
                                        }

                                      </td>


                                      {/* CREATED AT */}

                                      <td>

                                        {row?.createdAt
                                          ? new Date(
                                            row.createdAt
                                          ).toLocaleDateString(
                                            "en-IN"
                                          )
                                          : "-"}

                                      </td>


                                      {/* ITEM COUNT */}

                                      <td>

                                        {
                                          row?.items
                                            ?.length ||
                                          0
                                        }

                                      </td>


                                      {/* VIEW ITEMS */}

                                      <td className="align-middle text-center">

                                        <i
                                          className="fa fa-eye"
                                          style={{
                                            cursor:
                                              "pointer"
                                          }}
                                          title="View PO Items"
                                          onClick={(
                                            e
                                          ) =>
                                            handleViewItem(
                                              e,
                                              row
                                            )
                                          }
                                        />

                                      </td>


                                      {/* PRINT */}

                                      <td className="align-middle text-center">

                                        <button
                                          type="button"
                                          className="btn btn-success btn-xs"
                                          disabled={
                                            Boolean(
                                              printLoadingId
                                            )
                                          }
                                          onClick={() =>
                                            handleViewClick(
                                              row
                                            )
                                          }
                                          title="View Purchase Order"
                                        >

                                          {String(
                                            printLoadingId
                                          ) ===
                                            String(
                                              row?._id
                                            ) ? (

                                            <>

                                              <span
                                                className="spinner-border spinner-border-sm me-1"
                                                role="status"
                                                aria-hidden="true"
                                              />

                                              Loading...

                                            </>

                                          ) : (

                                            <>

                                              <i className="fa fa-print me-1" />

                                              Print

                                              <span
                                                className="badge bg-light text-dark ms-2"
                                                style={{
                                                  minWidth:
                                                    "24px",
                                                  fontSize:
                                                    "11px"
                                                }}
                                                title="Available PO versions"
                                              >

                                                {
                                                  row?.printCount ||
                                                  1
                                                }

                                              </span>

                                            </>

                                          )}

                                        </button>

                                      </td>


                                      {/* =================
                                              PO AMENDMENT
                                              PRINTABLE ONLY
                                          ================= */}

                                      {isPrintablePO &&
                                        userRole ===
                                        "pomaker" && (

                                          <td className="text-center align-middle">

                                            <button
                                              type="button"
                                              className="btn btn-warning btn-xs"
                                              onMouseEnter={
                                                preloadRateChangeModal
                                              }
                                              onFocus={
                                                preloadRateChangeModal
                                              }
                                              onClick={() =>
                                                handleRateChangeRequest(
                                                  row
                                                )
                                              }
                                            >

                                              Request

                                            </button>

                                          </td>

                                        )}

                                    </tr>

                                  );
                                }
                              )

                            ) : (

                              <tr>

                                <td
                                  colSpan={
                                    isPrintablePO &&
                                      userRole ===
                                      "pomaker"
                                      ? 16
                                      : 15
                                  }
                                  className="text-center"
                                >

                                  No Purchase Orders Found

                                </td>

                              </tr>

                            )}

                          </tbody>

                        </table>

                      </div>


                      {/* =============================
                              PAGINATION
                          ============================= */}

                      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">

                        <div className="dataTables_info">

                          Showing{" "}

                          {filteredSearch.length >
                            0
                            ? activePage *
                            sort +
                            1
                            : 0}

                          {" to "}

                          {Math.min(
                            filteredSearch.length,
                            (activePage +
                              1) *
                            sort
                          )}

                          {" of "}

                          {
                            filteredSearch.length
                          }

                          {" entries"}

                        </div>


                        <Pagination
                          activePage={
                            activePage
                          }
                          totalPages={
                            pagination
                          }
                          onPageChange={
                            onClick
                          }
                        />

                      </div>

                    </div>

                  </div>
                </div>

              </div>

            </Collapse>

          </div>

        </div>

      </div>

    </>
  );
};


export default PurchaseOrderManagement;